import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck, faClose, faLink } from "@fortawesome/free-solid-svg-icons"
import { faFacebook, faInstagram, faTiktok, faWhatsapp } from "@fortawesome/free-brands-svg-icons"
import { companyMemory } from "../../Objects/Memory/Memory"
import ModalPortal from "../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import { ButtonIcon } from "../../components/Button/Button"
import THEME from "../../configuration/Theme"
import { getPublicMenuTheme, isLegacyPublicMenuTheme } from "../Helpers/PublicMenuTheme"

interface SocialSharePupopProps {
  isOpen: boolean
  storeSlug?: string
  storeName?: string
  storeDescription?: string
  onClose: () => void
}

interface SocialLinkItem {
  key: string
  label: string
  url: string
  icon: any
  borderClass: string
  iconClass: string
  buttonClass: string
}

export const SocialSharePupop: React.FC<SocialSharePupopProps> = ({
  isOpen,
  storeSlug,
  storeName,
  storeDescription,
  onClose,
}) => {
  const { t } = useTranslation()

  const theme = getPublicMenuTheme(companyMemory)

  const isLegacyTheme = isLegacyPublicMenuTheme(theme)

  const [copyFeedback, setCopyFeedback] = useState("")

  const socialLinks = useMemo<SocialLinkItem[]>(() => {
    return [
      {
        key: "instagram",
        label: t("publicMenu:social.instagram"),
        url: companyMemory.companySocialMediaEntity?.instagramUrl || "",
        icon: faInstagram,
        borderClass: "border-red-500",
        iconClass: "text-red-600",
        buttonClass: "border-red-500 text-red-600 hover:bg-red-50",
      },
      {
        key: "facebook",
        label: t("publicMenu:social.facebook"),
        url: companyMemory.companySocialMediaEntity?.facebookUrl || "",
        icon: faFacebook,
        borderClass: "border-blue-600",
        iconClass: "text-blue-600",
        buttonClass: "border-blue-600 text-blue-600 hover:bg-blue-50",
      },
      {
        key: "whatsapp",
        label: t("publicMenu:social.whatsapp"),
        url: companyMemory.companySocialMediaEntity?.whatsappUrl || "",
        icon: faWhatsapp,
        borderClass: "border-green-500",
        iconClass: "text-green-600",
        buttonClass: "border-green-500 text-green-600 hover:bg-green-50",
      },
      {
        key: "tiktok",
        label: t("publicMenu:social.tiktok"),
        url: companyMemory.companySocialMediaEntity?.tiktokUrl || "",
        icon: faTiktok,
        borderClass: "border-black",
        iconClass: "text-black",
        buttonClass: "border-black text-black hover:bg-gray-50",
      },
    ].filter((item) => item.url && item.url.trim().length > 0)
  }, [t])

  const publicStoreLink = useMemo(() => {
    return `${window.location.origin}/${storeSlug ?? ""}`
  }, [storeSlug])

  const handleShowFeedback = (message: string) => {
    setCopyFeedback(message)

    setTimeout(() => {
      setCopyFeedback("")
    }, 2000)
  }

  const handleCopy = async (value: string = publicStoreLink) => {
    await navigator.clipboard.writeText(value)

    handleShowFeedback(t("publicMenu:social.copy_success"))
  }

  const handleShareByNetwork = async (network: string) => {
    const encodedLink = encodeURIComponent(publicStoreLink)

    const shareMap: Record<string, string> = {
      whatsapp: `https://wa.me/?text=${encodedLink}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedLink}`,
    }

    const shareUrl = shareMap[network]

    if (shareUrl) {
      window.open(shareUrl, "_blank")

      return
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title: storeName || t("publicMenu:social.default_title"),
          text: storeDescription || undefined,
          url: publicStoreLink,
        })

        return
      } catch {}
    }

    await navigator.clipboard.writeText(publicStoreLink)

    handleShowFeedback(t("publicMenu:social.copy_success"))
  }

  if (!isOpen) {
    return null
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.25s">
          <div
            className="w-[95vw] max-w-md rounded-lg bg-white p-5 ring-2 ring-orange-500"
            style={
              isLegacyTheme
                ? undefined
                : {
                    backgroundColor: theme.surfaceColor,
                    boxShadow: `0 0 0 2px ${theme.borderColor}`,
                    color: theme.textColor,
                  }
            }
          >
            <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
              <div className="mb-5 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2
                    className="text-base font-black uppercase tracking-wide text-gray-900"
                    style={isLegacyTheme ? undefined : { color: theme.storeNameColor }}
                  >
                    {t("publicMenu:social.title")}
                  </h2>

                  <p
                    className="mt-1 text-xs text-gray-500"
                    style={isLegacyTheme ? undefined : { color: theme.textColor, opacity: 0.7 }}
                  >
                    {storeName || t("publicMenu:social.description")}
                  </p>
                </div>

                <ButtonIcon
                  iconColor={THEME.colors.buttonFGCloseColor}
                  size={"md"}
                  icon={faClose}
                  onClick={onClose}
                  descriptionKey="interactionDescriptions:publicMenu.pupop.socialSharePupop.button.onClose"
                />
              </div>
            </Animate>

            {storeDescription && (
              <Animate type={AnimationTypes.fadeInTraslate} duration="1.4s">
                <p
                  className="mb-4 text-xs leading-relaxed text-gray-600"
                  style={isLegacyTheme ? undefined : { color: theme.textColor, opacity: 0.8 }}
                >
                  {storeDescription}
                </p>
              </Animate>
            )}

            <Animate type={AnimationTypes.fadeInTraslate} duration="1.6s">
              <div className="mb-5">
                <p
                  className="mb-3 text-[11px] font-black uppercase tracking-wide text-gray-500"
                  style={isLegacyTheme ? undefined : { color: theme.textColor, opacity: 0.7 }}
                >
                  {t("publicMenu:social.open_channels")}
                </p>

                {socialLinks.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3">
                    {socialLinks.map((item) => (
                      <a
                        key={item.key}
                        href={item.url}
                        target="_blank"
                        rel="noreferrer"
                        className={`rounded-xl border-2 bg-white px-3 py-3 transition hover:-translate-y-[1px] ${item.borderClass}`}
                        data-interaction-description-key="interactionDescriptions:publicMenu.pupop.socialSharePupop.link.link"
                      >
                        <div className="flex items-center gap-3">
                          <FontAwesomeIcon icon={item.icon} className={`text-lg ${item.iconClass}`} />

                          <span className="text-xs font-black text-gray-800">{item.label}</span>
                        </div>
                      </a>
                    ))}
                  </div>
                ) : (
                  <div
                    className="rounded-xl border-2 border-gray-300 bg-white px-4 py-3 text-center text-xs font-semibold text-gray-500"
                    style={
                      isLegacyTheme
                        ? undefined
                        : { borderColor: theme.borderColor, backgroundColor: theme.surfaceColor, color: theme.textColor }
                    }
                  >
                    {t("publicMenu:social.no_channels")}
                  </div>
                )}
              </div>
            </Animate>

            {socialLinks.length > 0 && (
              <Animate type={AnimationTypes.fadeInTraslate} duration="1.8s">
                <div className="mb-5">
                  <p
                    className="mb-3 text-[11px] font-black uppercase tracking-wide text-gray-500"
                    style={isLegacyTheme ? undefined : { color: theme.textColor, opacity: 0.7 }}
                  >
                    {t("publicMenu:social.share_on")}
                  </p>

                  <div className="grid grid-cols-2 gap-3">
                    {socialLinks.map((item) => (
                      <button
                        key={`share-${item.key}`}
                        type="button"
                        onClick={() => handleShareByNetwork(item.key)}
                        className={`rounded-xl border-2 bg-white px-3 py-3 text-left transition hover:-translate-y-[1px] ${item.buttonClass}`}
                        data-interaction-description-key="interactionDescriptions:publicMenu.pupop.socialSharePupop.button.handleShareByNetworkItemKey"
                      >
                        <div className="flex items-center gap-3">
                          <FontAwesomeIcon icon={item.icon} className="text-lg" />
                          <span className="text-xs font-black">{item.label}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </Animate>
            )}

            <Animate type={AnimationTypes.fadeInTraslate} duration="2s">
              <div>
                <p
                  className="mb-3 text-[11px] font-black uppercase tracking-wide text-gray-500"
                  style={isLegacyTheme ? undefined : { color: theme.textColor, opacity: 0.7 }}
                >
                  {t("publicMenu:social.copy_link")}
                </p>

                <div className="flex flex-col gap-3">
                  <div
                    className="rounded-xl border-2 border-slate-500 bg-white px-3 py-3 text-xs text-gray-700"
                    style={
                      isLegacyTheme
                        ? undefined
                        : { borderColor: theme.borderColor, backgroundColor: theme.surfaceColor, color: theme.textColor }
                    }
                  >
                    <span className="block truncate">{publicStoreLink}</span>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleCopy()}
                    className="rounded-xl border-2 border-slate-500 bg-white px-4 py-3 text-xs font-black text-slate-600 transition hover:border-slate-800"
                    style={
                      isLegacyTheme
                        ? undefined
                        : {
                            borderColor: theme.shareButtonBackgroundColor,
                            backgroundColor: theme.shareButtonBackgroundColor,
                            color: theme.shareButtonTextColor,
                          }
                    }
                    data-interaction-description-key="interactionDescriptions:publicMenu.pupop.socialSharePupop.button.handleCopy"
                  >
                    <FontAwesomeIcon icon={faLink} className="mr-2" />
                    {t("publicMenu:social.copy_link")}
                  </button>
                </div>

                {copyFeedback && (
                  <div className="mt-3 flex items-center gap-2 rounded-xl border-2 border-green-500 bg-white px-3 py-2">
                    <FontAwesomeIcon icon={faCheck} className="text-green-600" />

                    <p className="text-[11px] font-bold text-green-700">{copyFeedback}</p>
                  </div>
                )}
              </div>
            </Animate>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
